import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import { useParams } from 'react-router-dom'
import { Card, CardBody, CardHeader } from '../../components/other/card/card'
import {
  useCreateCampaignMutation,
  useGetCampaignByIdQuery,
  useUpdateCampaignMutation
} from '../../redux/apis/campaignsManagement/campaigns/cMCampaignsApiSlice'
import Loader from '../pages/loader'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { getUserId } from '../../helpers/auth'
import { DatePicker, Image, Input, Select } from 'antd'
import dayjs from 'dayjs'
import CampaignTypesDropdown from '../../components/campaigns/info/CampaignTypesDropdown'
import CountriesDropdown from '../../components/CountriesDropdown'
import { toast } from 'react-toastify'
import StepTwo from '../../components/campaigns/info/StepTwo'
import { useNavigate } from 'react-router'
const steps = [
  { id: 1, label: 'Campaign Information' },
  { id: 2, label: 'Customers & Services Information' }
]

const CampaignInfoEdit = React.memo(() => {
  let navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(1)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState('')
  const context = useContext(AppSettings)
  const { id } = useParams()
  const [
    updateCampaign,
    { isLoading: updateLoading, error: updateError, isSuccess }
  ] = useUpdateCampaignMutation()
  const [
    createCampaign,
    { isLoading: createLoading, error: createError, isSuccess: createIsSuccess }
  ] = useCreateCampaignMutation()
  const {
    data: campaignData,
    isLoading,
    error,
    refetch
  } = useGetCampaignByIdQuery(
    {
      id: id,
      expand: [
        'customer_services','customer_services.customer'
      ].join(',')
    },
    {
      skip: id == 0
    }
  )

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      campaign_name: campaignData?.data?.campaign_name || '',
      campaign_actors: campaignData?.data?.campaign_actors || '',
      sample_of_work: campaignData?.data?.sample_of_work || '',
      sample_of_pirated_url: campaignData?.data?.sample_of_pirated_url || '',
      number_of_seasons: campaignData?.data?.number_of_seasons || '',
      no_of_episodes: campaignData?.data?.no_of_episodes || '',
      campaign_release_date: campaignData?.data?.campaign_release_date || '',
      campaign_rights_owner: campaignData?.data?.campaign_rights_owner || '',
      campaign_type: campaignData?.data?.campaign_type || '',
      country: campaignData?.data?.country || '',
      campaign_poster: campaignData?.data?.campaign_poster || '',
      crawling_priority: campaignData?.data?.crawling_priority || '',
      last_updated_by_id: getUserId()
    },

    validationSchema: Yup.object({
      campaign_name: Yup.string().required('Please Enter Campaign Name'),
      campaign_actors: Yup.string().nullable(),
      sample_of_work: Yup.string().required('Please Enter Work Sample'),
      sample_of_pirated_url: Yup.string().nullable(),
      number_of_seasons: Yup.string().nullable(),
      no_of_episodes: Yup.string().nullable(),
      campaign_release_date: Yup.string().required(
        'Please Enter Campaign Release Date'
      ),
      campaign_rights_owner: Yup.string().required(
        'Please Enter Campaign Rights Owner'
      ),
      campaign_type: Yup.string().required('Please Enter Campaign Type'),
      country: Yup.string().required('Please Enter Country'),
      crawling_priority: Yup.number().nullable(),
    }),

    onSubmit: async values => {
      const formData = new FormData()
      if (file) {
        formData.append('campaign_poster', file)
      } else {
        if(!values.campaign_poster){
          toast.error('Please sellect campaign poster')
          return
        }
        delete values.campaign_poster
      }
      for (let key in values) {
        formData.append(key, values[key])
      }


      try {
        if (id == 0) {
          const res = await createCampaign(formData).unwrap()
          navigate(`/campaigns/campaigns-info/${res?.data?.id}`)

        } else {
          await updateCampaign({ id: campaignData?.data?.id, body: formData }).unwrap()
        }
      } catch (error) {
        console.error(error)
      }
    }
  })

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (updateError?.data) {
      const errorMessages = Object.values(updateError.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (createError?.data) {
      const errorMessages = Object.values(createError.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error, updateError, createError])

  useEffect(() => {
    if (isSuccess) {
      toast.success('Campaign Saved successfully!')
      setFile(null)
      refetch()
    } else if (createIsSuccess) {
      toast.success('Campaign Created successfully!')
      setFile(null)
      if (activeStep < steps.length) setActiveStep(activeStep + 1)
    }
  }, [createIsSuccess, isSuccess])

  useEffect(() => {
    if (!file) {
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  // ponytail: formik `dirty` compares against initialValues, which reinitialize after refetch
  const hasChanges = validation.dirty || !!file

  const handleNext = () => {
    if (activeStep === 1) {
      setActiveStep(2)
    } else {
      navigate('/campaigns/campaigns-info')
    }
  }
  const handlePrev = () => {
    if (activeStep > 1) setActiveStep(activeStep - 1)
  }
  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context]) // Add context as a dependency

  return (
    <div>
      <Loader isLoading={isLoading || updateLoading || createLoading} />
      <div className='row'>
        <Card>
          <CardHeader>{id == 0 ? 'CREATE CAMPAIGN': 'EDIT CAMPAIGN |'} {campaignData?.data?.campaign_name}</CardHeader>
          <CardBody>
            <div className='nav-wizards-container p-4'>
              <nav className='nav nav-wizards-1 mb-4 flex'>
                {steps.map(step => (
                  <div key={step.id} className='nav-item flex-1 text-center'>
                    <div
                      className={`nav-link ${
                        step.id < activeStep
                          ? 'completed'
                          : step.id === activeStep
                          ? 'active'
                          : 'disabled'
                      }`}
                    >
                      <div className='nav-no text-lg font-bold'>{step.id}</div>
                      <div className='nav-text text-sm'>{step.label}</div>
                    </div>
                  </div>
                ))}
              </nav>

              <Card>
                <CardBody className='p-6'>
                  {activeStep === 1 && (
                    <div>
                      <div className='flex w-full gap-8'>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Campaign Name</label>
                          <Input
                            type='text'
                            name='campaign_name'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.campaign_name}
                          />
                          {validation.touched.campaign_name &&
                          validation.errors.campaign_name ? (
                            <p className='text-danger'>
                              {validation.errors.campaign_name}
                            </p>
                          ) : null}
                        </div>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Campaign Actors</label>
                          <Input
                            type='text'
                            name='campaign_actors'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.campaign_actors}
                          />
                          {validation.touched.campaign_actors &&
                          validation.errors.campaign_actors ? (
                            <p className='text-danger'>
                              {validation.errors.campaign_actors}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className='flex w-full gap-8'>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Work Sample</label>
                          <Input
                            type='text'
                            name='sample_of_work'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.sample_of_work}
                          />
                          {validation.touched.sample_of_work &&
                          validation.errors.sample_of_work ? (
                            <p className='text-danger'>
                              {validation.errors.sample_of_work}
                            </p>
                          ) : null}
                        </div>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Pirated Url Sample</label>
                          <Input
                            type='text'
                            name='sample_of_pirated_url'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.sample_of_pirated_url}
                          />
                          {validation.touched.sample_of_pirated_url &&
                          validation.errors.sample_of_pirated_url ? (
                            <p className='text-danger'>
                              {validation.errors.sample_of_pirated_url}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className='flex w-full gap-8'>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Number Of Seasons</label>
                          <Input
                            type='text'
                            name='number_of_seasons'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.number_of_seasons}
                          />
                          {validation.touched.number_of_seasons &&
                          validation.errors.number_of_seasons ? (
                            <p className='text-danger'>
                              {validation.errors.number_of_seasons}
                            </p>
                          ) : null}
                        </div>
                        <div className=' mb-3 flex-1 customInput'>
                          <label className="form-label">Number Of Episodes</label>
                          <Input
                            type='text'
                            name='no_of_episodes'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.no_of_episodes}
                          />
                          {validation.touched.no_of_episodes &&
                          validation.errors.no_of_episodes ? (
                            <p className='text-danger'>
                              {validation.errors.no_of_episodes}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className='flex w-full gap-8'>
                        <div className=' mb-3 customeDate flex-1'>
                          <label className="form-label">Campaign Release Date</label>
                          <br />
                          <DatePicker
                            name='campaign_release_date'
                            style={{ width: '100%' }}
                            onChange={(e, date) =>
                              validation.setFieldValue(
                                'campaign_release_date',
                                date
                              )
                            }
                            onBlur={validation.handleBlur}
                            value={
                              validation.values.campaign_release_date
                                ? dayjs(validation.values.campaign_release_date)
                                : ''
                            }
                          />
                          {validation.touched.campaign_release_date &&
                          validation.errors.campaign_release_date ? (
                            <p className='text-danger'>
                              {validation.errors.campaign_release_date}
                            </p>
                          ) : null}
                        </div>
                        <div className=' mb-3  customInput flex-1'>
                          <label className="form-label">Campaign Rights Owner</label>
                          <Input
                            type='text'
                            name='campaign_rights_owner'
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.campaign_rights_owner}
                          />
                          {validation.touched.campaign_rights_owner &&
                          validation.errors.campaign_rights_owner ? (
                            <p className='text-danger'>
                              {validation.errors.campaign_rights_owner}
                            </p>
                          ) : null}
                        </div>
                        <div className='customSelect flex-1'>
                          <CampaignTypesDropdown
                            onChange={validation.setFieldValue}
                            onBlur={validation.handleBlur}
                            value={validation.values.campaign_type}
                          />
                          {validation.touched.campaign_type &&
                          validation.errors.campaign_type ? (
                            <p className='text-danger'>
                              {validation.errors.campaign_type}
                            </p>
                          ) : null}
                        </div>
                        <div className='customSelect flex-1'>
                          <CountriesDropdown
                            onChange={e =>
                              validation.setFieldValue('country', e)
                            }
                            onBlur={validation.handleBlur}
                            value={validation.values.country}
                          />
                          {validation.touched.country &&
                          validation.errors.country ? (
                            <p className='text-danger'>
                              {validation.errors.country}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className='flex w-full gap-8 mb-3'>
                        <div className='customSelect flex-1'>
                          <label className="form-label">Crawling Priority</label>
                          <Select
                            name='crawling_priority'
                            style={{ width: '100%' }}
                            placeholder='Select Crawling Priority'
                            onChange={value =>
                              validation.setFieldValue('crawling_priority', value)
                            }
                            onBlur={validation.handleBlur}
                            value={validation.values.crawling_priority || undefined}
                            options={[
                              { label: '1 - Low', value: 1 },
                              { label: '2 - Medium', value: 2 },
                              { label: '3 - High', value: 3 }
                            ]}
                          />
                          {validation.touched.crawling_priority &&
                          validation.errors.crawling_priority ? (
                            <p className='text-danger'>
                              {validation.errors.crawling_priority}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className=' mb-3'>
                        <label className="form-label">Upload Campaign Poster</label>
                        <div className='flex gap-4 customInput'>
                          <Input
                            type='file'
                            onChange={e => setFile(e.target.files[0])}
                            accept='.png, .jpg, .jpeg'
                            onBlur={validation.handleBlur}
                            required
                            style={{ height: '40px' }}
                          />
                          <Image
                            className='border-1'
                            style={{ width: '200px' }}
                            src={
                              preview
                                ? preview
                                : validation?.values?.campaign_poster
                            }
                          />
                        </div>
                        {validation.touched.campaign_poster && validation.errors.campaign_poster ? (
                          <p className='text-danger'>
                            {validation.errors.campaign_poster}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div>
                      <StepTwo data={campaignData?.data} refetch={refetch} />
                    </div>
                  )}
                </CardBody>
              </Card>

              <div className='flex justify-end gap-8 mt-4'>
                <button
                  onClick={handlePrev}
                  disabled={activeStep === 1}
                  className='btn btn-theme'
                >
                  Previous
                </button>
                {activeStep === 1 && hasChanges && (
                  <button
                    onClick={validation.handleSubmit}
                    className='btn btn-theme'
                  >
                    Save
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={activeStep === 1 && id == 0}
                  className='btn btn-theme'
                >
                  {activeStep === 1 ? 'Next' : 'Finish'}
                </button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
})

export default CampaignInfoEdit
